'use client';
import React, { useState } from 'react';


const JUZ_DATA = Array.from({ length: 30 }, (_, i) => ({
  id: `juz-${i + 1}`,
  number: i + 1,
  status: i < 7 ? 'mastered' : i < 11 ? 'in-progress' : i === 11 ? 'current' : 'locked',
}));

const statusColors: Record<string, string> = {
  mastered: 'bg-primary',
  'in-progress': 'bg-amber-400',
  current: 'bg-teal animate-pulse-soft',
  locked: 'bg-border',
};

export default function StudentHeroCard() {
  const [hoveredJuz, setHoveredJuz] = useState<number | null>(null);

  const masteredCount = JUZ_DATA.filter((j) => j.status === 'mastered').length;
  const inProgressCount = JUZ_DATA.filter((j) => j.status === 'in-progress').length;
  const percent = Math.round(((masteredCount + inProgressCount * 0.5) / 30) * 100);

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-5">
        {/* Left: progress info */}
        <div className="lg:col-span-2 gradient-green p-6 text-white">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">📖</span>
            <div>
              <p className="text-sm font-500 text-green-100">Hifdh Progress</p>
              <h2 className="text-xl font-700">Al-Quran Al-Kareem</h2>
            </div>
          </div>

          <div className="flex items-end gap-2 mb-2">
            <span className="text-5xl font-800 tabular-nums">{masteredCount}</span>
            <span className="text-lg text-green-200 mb-1">/ 30 Juz</span>
          </div>
          <p className="text-green-100 text-sm mb-4">{percent}% of full Quran memorized</p>

          {/* Progress bar */}
          <div className="w-full bg-white/20 rounded-full h-2 mb-4">
            <div
              className="bg-gold h-2 rounded-full transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-white/10 rounded-xl p-2">
              <p className="text-lg font-700">{masteredCount}</p>
              <p className="text-xs text-green-100">Mastered</p>
            </div>
            <div className="bg-white/10 rounded-xl p-2">
              <p className="text-lg font-700">{inProgressCount}</p>
              <p className="text-xs text-green-100">In Review</p>
            </div>
            <div className="bg-white/10 rounded-xl p-2">
              <p className="text-lg font-700">{30 - masteredCount - inProgressCount}</p>
              <p className="text-xs text-green-100">Remaining</p>
            </div>
          </div>
        </div>

        {/* Right: Juz grid */}
        <div className="lg:col-span-3 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-600 text-foreground">Juz Map — 30 Parts of the Quran</h3>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-primary inline-block" /> Mastered</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> Reviewing</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-teal inline-block" /> Current</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-border inline-block" /> Locked</span>
            </div>
          </div>
          <div className="grid grid-cols-6 gap-2">
            {JUZ_DATA.map((juz) => (
              <div
                key={juz.id}
                className={`relative rounded-xl h-12 flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-110 hover:shadow-md ${statusColors[juz.status]}`}
                onMouseEnter={() => setHoveredJuz(juz.number)}
                onMouseLeave={() => setHoveredJuz(null)}
              >
                <span className={`text-sm font-700 ${juz.status === 'locked' ? 'text-muted-foreground' : 'text-white'}`}>
                  {juz.number}
                </span>
                {hoveredJuz === juz.number && (
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded-lg whitespace-nowrap z-10">
                    Juz {juz.number} · {juz.status === 'mastered' ? '✓ Mastered' : juz.status === 'in-progress' ? '↻ Reviewing' : juz.status === 'current' ? '▶ Current' : '🔒 Locked'}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3 p-3 bg-teal/10 border border-teal/20 rounded-xl">
            <span className="text-xl">🎯</span>
            <div>
              <p className="text-sm font-600 text-teal">Current Target: Juz 12 — Surah Hud</p>
              <p className="text-xs text-muted-foreground">Next review session: Today at 4:00 PM · 3 pages due</p>
            </div>
            <button className="ml-auto px-3 py-1.5 bg-teal text-white rounded-lg text-xs font-600 hover:bg-teal/90 transition-colors active:scale-95">
              Start Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}